/**
 * THE SWIPE — the arithmetic behind each survey row on Home (Task 3).
 *
 * The two backup buttons this task removes ("Save a backup instead" under the
 * unlinked card, "Back up or move surveys" at the foot of the list) become a
 * rail behind each row: swipe LEFT and Back up, then Delete, are revealed.
 * Follows the split `PhotoFan` uses (src/ui/state/photoFan.ts /
 * src/ui/components/PhotoFan.tsx): the ARITHMETIC — where the surface sits,
 * what counts as open, which way a fling resolves, which actions the rail
 * shows and whether a tap on one asks first — lives here, pure and
 * React/DOM-free; `SwipeableRow.tsx` does pointer events and GSAP only,
 * applying what these functions return.
 *
 * ── Only left, only two actions ─────────────────────────────────────────────
 * Nothing is revealed by swiping right — there is no left-hand rail — so the
 * surface's whole legal travel is `[-railWidth, 0]`; a positive drag is
 * clamped straight back to closed rather than let it overshoot past resting.
 *
 * ── Open, or not ─────────────────────────────────────────────────────────────
 * A released drag settles by ONE of two rules, exactly as a native swipe-to-
 * reveal list decides it: a FLING (faster than `FLING_PX_PER_S`, either
 * direction) decides outright, whatever fraction of the rail it had actually
 * travelled; short of a fling, it is the distance — past `OPEN_FRACTION` of
 * the rail's width, the row snaps open, short of that it snaps back closed.
 */

export type SwipeState = 'closed' | 'open';

/** Past this fraction of the rail's width, a released (non-fling) drag opens. */
export const OPEN_FRACTION = 0.46;

/**
 * A release faster than this, in px/s, decides the swipe outright — the way
 * flicking a real drawer does — whatever distance it had travelled. Negative
 * is leftward (opening); positive is rightward (closing).
 */
export const FLING_PX_PER_S = 720;

/**
 * The surface's legal travel: 0 (closed) to `-railWidth` (fully open, the
 * rail's whole width showing). Swiping right reveals nothing, so any positive
 * offset is clamped straight back to closed rather than allowed to overshoot.
 */
export function clampOffset(offset: number, railWidth: number): number {
  if (railWidth <= 0) return 0;
  if (offset > 0) return 0;
  if (offset < -railWidth) return -railWidth;
  return offset;
}

/**
 * Where a released drag settles — see the header for the two rules. A rail
 * with no width (nothing to reveal) never opens.
 */
export function resolveSwipe(offset: number, velocityPxPerSec: number, railWidth: number): SwipeState {
  if (railWidth <= 0) return 'closed';
  if (velocityPxPerSec <= -FLING_PX_PER_S) return 'open';
  if (velocityPxPerSec >= FLING_PX_PER_S) return 'closed';
  return -offset / railWidth >= OPEN_FRACTION ? 'open' : 'closed';
}

/** The surface's resting x for a state, given the rail's real width. */
export function settleOffset(state: SwipeState, railWidth: number): number {
  return state === 'open' ? -railWidth : 0;
}

/* ══════════════════════════════ the rail's contents ═══════════════════════ */

export type RailAction = 'backup' | 'delete';

/**
 * Which actions the rail shows, in the order the brief names them: Back up,
 * then Delete. `blocked` is `removalView(record, title).blockedNote !== null`
 * — a submitted-and-sent survey, the same state `RemoveSurvey` already
 * renders with NO control at all rather than a disabled button (D-012). The
 * rail keeps that choice: a blocked survey can still be backed up, and simply
 * has nothing to delete.
 */
export function railActions(blocked: boolean): readonly RailAction[] {
  return blocked ? ['backup'] : ['backup', 'delete'];
}

export type RailEffect = 'ask' | 'run';

/**
 * What tapping a rail action does. Delete is destructive and ALWAYS asks
 * first — it never fires on the first tap, exactly like `RemoveSurvey`'s own
 * button, which opens `ConfirmPanel` rather than removing anything. Back up
 * is not destructive: it runs the moment it is tapped, exactly like
 * `SaveSurveyCopy`'s button.
 */
export function railTap(action: RailAction): RailEffect {
  return action === 'delete' ? 'ask' : 'run';
}
